import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "../shared/Logo";
import LangLink from "../shared/LangLink";
import CTAButton from "../shared/CTAButton";
import LanguageSwitcher from "./LanguageSwitcher";
import { MAIN_NAV } from "../../data/navigation";
import { useI18n } from "../../i18n";

export default function Header() {
  const { t } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      {/* Announcement bar */}
      <div className="bg-primary text-primary-foreground/90 text-xs md:text-[13px] font-sans" data-testid="announcement-bar">
        <div className="container-psv flex items-center justify-center md:justify-between gap-3 py-2.5 text-center">
          <span className="tracking-wide">{t("announcement.text")}</span>
          <LangLink to="/patient-journey" className="hidden md:inline-flex items-center gap-1 text-accent hover:underline underline-offset-4">
            {t("announcement.link")}
            <ArrowUpRight className="w-3.5 h-3.5" />
          </LangLink>
        </div>
      </div>

      {/* Main header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-background/90 backdrop-blur-xl border-b border-border py-3" : "bg-background border-b border-transparent py-5"
        }`}
        data-testid="site-header"
      >
        <div className="container-psv flex items-center justify-between gap-6">
          <Logo />

          <nav className="hidden lg:flex items-center gap-5 xl:gap-7 font-sans text-[14px] xl:text-[15px]" aria-label="Main">
            {MAIN_NAV.map((item) => (
              <LangLink
                key={item.key}
                to={item.to}
                className="text-primary/80 hover:text-accent transition-colors duration-200 whitespace-nowrap"
                data-testid={`nav-${item.key}`}
              >
                {t(item.label)}
              </LangLink>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <LanguageSwitcher className="hidden md:flex" />
            <CTAButton to="/#case-review" size="sm" className="hidden md:inline-flex" testId="header-case-review-btn">
              {t("cta.caseReview")}
            </CTAButton>
            <button
              className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-border text-primary"
              onClick={() => setOpen(true)}
              aria-label={t("cta.menu")}
              data-testid="mobile-menu-open"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu overlay */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] bg-background lg:hidden flex flex-col"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            data-testid="mobile-menu"
          >
            <div className="container-psv flex items-center justify-between py-5 border-b border-border">
              <Logo />
              <button
                className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-border text-primary"
                onClick={() => setOpen(false)}
                aria-label={t("cta.close")}
                data-testid="mobile-menu-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="container-psv flex-1 overflow-y-auto py-8 flex flex-col gap-1" aria-label="Mobile">
              {MAIN_NAV.map((item, i) => (
                <motion.div
                  key={item.key}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + i * 0.04 }}
                >
                  <LangLink
                    to={item.to}
                    onClick={() => setOpen(false)}
                    className="block font-serif text-3xl text-primary py-3 border-b border-border/60 hover:text-accent transition-colors"
                    data-testid={`mobile-nav-${item.key}`}
                  >
                    {t(item.label)}
                  </LangLink>
                </motion.div>
              ))}
            </nav>
            <div className="container-psv py-6 border-t border-border flex items-center justify-between gap-4">
              <LanguageSwitcher onNavigate={() => setOpen(false)} />
              <CTAButton to="/#case-review" size="sm" onClick={() => setOpen(false)} testId="mobile-case-review-btn">
                {t("cta.caseReview")}
              </CTAButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
